/**
 * Shared navy shell navigation language.
 *
 * Used by SidebarNavigation and InvestorAppShell so hover / selected /
 * sideline / radius / motion stay one system.
 */

import { cn } from '@/lib/utils';

/** Horizontal gutter inside `.ax-nav-shell nav` — keep in sync with CSS `--ax-nav-gutter`. */
export const SHELL_NAV_GUTTER_CLASS = {
  expanded: 'px-2',
  collapsed: 'px-1.5',
} as const;

export const SHELL_NAV = {
  itemFocus:
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sidebar-bg)]',

  itemExpanded: 'ax-nav-item group flex items-center gap-2.5 px-2.5 py-2',

  itemCollapsed: 'ax-nav-item group flex items-center justify-center w-10 h-10 mx-auto',

  /** Fill and inset selection frame share exactly the item's corner geometry. */
  activeBg: 'absolute inset-0 bg-[var(--sidebar-item-active-bg)] rounded-[inherit]',

  activeBgCollapsed:
    'absolute inset-0 bg-[var(--sidebar-item-active-bg)] rounded-[inherit]',

  /** Curved leading accent rail attached to the current expanded item. */
  activeRail: 'ax-nav-active-rail ax-nav-active-rail--flush',

  /** Collapsed mark stays on the item, not the shell edge. */
  activeDot: 'ax-nav-active-rail ax-nav-active-rail--dot',

  icon: 'w-[18px] h-[18px] flex-shrink-0 relative z-10 transition-colors duration-[120ms] ease-out',

  label: 'text-[13px] font-medium truncate relative z-10 transition-colors duration-[120ms] ease-out',

  toggle:
    'absolute -right-3 top-6 z-20 w-6 h-6 rounded-full bg-[var(--sidebar-bg)] border border-[color:var(--ax1-shell-border)] flex items-center justify-center shadow-sm transition-shadow duration-[120ms] ease-out text-[color:var(--ax1-shell-muted)] hover:text-[color:var(--ax1-shell-text)] hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sidebar-bg)]',

  sectionLabel:
    'ax-section-label text-[color:var(--ax1-shell-muted)] select-none',

  groupLabel:
    'ax-section-label text-[color:var(--ax1-shell-muted)] select-none',

  groupRule: 'my-1.5 mx-2 border-t border-[color:var(--ax1-shell-border)]',

  profileRing: 'ring-1 ring-[color:var(--ax1-shell-border)]',

  profileName: 'text-[13px] font-medium text-[color:var(--ax1-shell-text)] truncate leading-tight',

  /** Current operating capacity under the signed-in name (not a role chip stack). */
  profileCapacity: 'text-[11px] text-[color:var(--ax1-shell-muted)] truncate leading-tight mt-0.5',
} as const;

export function shellNavItemTone(isActive: boolean, opts?: { collapsed?: boolean }) {
  if (isActive) {
    return cn(
      'ax-nav-item--active',
      !opts?.collapsed && 'text-[var(--sidebar-item-active-text)]',
    );
  }
  if (opts?.collapsed) {
    return 'hover:bg-[var(--sidebar-item-hover-bg)]';
  }
  return 'text-[color:var(--ax1-shell-muted)] hover:bg-[var(--sidebar-item-hover-bg)] hover:text-[color:var(--ax1-shell-text)]';
}

export function shellNavIconTone(isActive: boolean) {
  return isActive
    ? 'text-[var(--sidebar-item-active-icon)]'
    : 'text-[color:var(--ax1-shell-muted)] group-hover:text-[color:var(--ax1-shell-text)]';
}

export function shellNavLabelTone(isActive: boolean) {
  return isActive
    ? 'text-[var(--sidebar-item-active-text)]'
    : 'text-[color:var(--ax1-shell-muted)] group-hover:text-[color:var(--ax1-shell-text)]';
}

export function shellNavScrollerClass(isCollapsed: boolean, extra?: string) {
  return cn(
    'flex-1 overflow-y-auto overflow-x-hidden',
    'scrollbar-thin scrollbar-thumb-[color:var(--ax1-shell-border)]',
    isCollapsed ? SHELL_NAV_GUTTER_CLASS.collapsed : SHELL_NAV_GUTTER_CLASS.expanded,
    extra,
  );
}
